import React, { useEffect, useState } from 'react';

import { Anchor, Box, Flex, Stack, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconExternalLink, IconFile } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { DroxyBoxedIcon } from 'components/Droxy/DroxyBoxedIcon';
import { ErrorAlert } from 'components/ErrorAlert';
import { ExtractedContentLayout } from 'components/ExtractedContent/ExtractedContentLayout';
import { ExtractedContentPreview } from 'components/ExtractedContent/ExtractedContentPreview/ExtractedContentPreview';
import { NavigationItem } from 'components/ExtractedContent/ExtractedItemsNavigation/NavigationItem';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { ResourceViewerWrapper } from 'components/ResourceViewerWrapper/ResourceViewerWrapper';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { GoogleDriveService } from 'services/google-drive-service';
import { ResourceService } from 'services/resource-service';
import { GOOGLE_DRIVE_FILES_QUERY_KEY, RESOURCE_QUERY_KEY } from 'shared/constants/query-keys';
import { GoogleDriveMimeType, googleDriveMimeTypeToReadableName } from 'shared/interfaces/GoogleDrive';

export function GoogleDriveViewerPage() {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { resourceId = '' } = useParams();
  const [selectedFileId, setSelectedFileId] = useState<string | undefined>(undefined);

  const searchForm = useForm({ initialValues: { search: '' } });

  const resourceQuery = useQuery({
    queryKey: [RESOURCE_QUERY_KEY, { resourceId: resourceId }],
    queryFn: () => ResourceService.getResourceInfo(resourceId),
    refetchOnWindowFocus: false,
    enabled: !!resourceId
  });

  const googleDriveFilesQuery = useQuery({
    queryKey: [GOOGLE_DRIVE_FILES_QUERY_KEY, { resourceId: resourceId }],
    queryFn: () => GoogleDriveService.getGoogleDriveFilesTextData(resourceId),
    enabled: !!resourceId,
    retry: false
  });

  // Set the first file as current when data loads
  useEffect(() => {
    const files = googleDriveFilesQuery.data?.files;
    if (files && files.length > 0 && !selectedFileId) {
      setSelectedFileId(files[0].fileId);
    }
  }, [googleDriveFilesQuery.data]);

  const handleFileClick = (fileId: string) => {
    setSelectedFileId(fileId);
  };

  const getFileIcon = (mimeType: GoogleDriveMimeType) => {
    switch (mimeType) {
      case GoogleDriveMimeType.PDF:
        return <IconFile color={theme.colors.red[6]} />;
      case GoogleDriveMimeType.DOCUMENT:
        return <IconFile color={theme.colors.blue[6]} />;
      case GoogleDriveMimeType.SLIDES:
        return <IconFile color={theme.colors.yellow[6]} />;
      default:
        return <IconFile color={theme.colors.gray[6]} />;
    }
  };

  if (!resourceId) return <ErrorAlert />;
  if (googleDriveFilesQuery.isLoading || resourceQuery.isLoading) return <FullScreenLoader />;
  if (googleDriveFilesQuery.isError || resourceQuery.isError) return <ErrorAlert />;

  const files = googleDriveFilesQuery.data?.files || [];
  const filteredFiles = files.filter((file) =>
    (file.name || '').toLowerCase().includes(searchForm.values.search.toLowerCase())
  );

  // Make sure the resource has a google drive file
  const googleDriveFileResource = resourceQuery.data?.googleDriveResourceFile;
  if (!googleDriveFileResource) return <ErrorAlert />;

  const selectedGoogleDriveFile = files.find((file) => file.fileId === selectedFileId);
  const webViewLink = googleDriveFileResource.webViewLink;

  return (
    <ResourceViewerWrapper
      resourceId={resourceId}
      backgroundColor={theme.colors.gray[0]}
      fileName={googleDriveFileResource.fileName || selectedGoogleDriveFile?.name}>
      <ExtractedContentLayout
        navigationTitle={t('GOOGLE_DRIVE.VIEWER.FILES_PANEL.TITLE')}
        navigationTooltip={t('GOOGLE_DRIVE.VIEWER.FILES_PANEL.TOOLTIP')}
        searchForm={searchForm}
        navigationItems={
          <Stack spacing={0}>
            {filteredFiles.map((file) => (
              <Box key={file.fileId}>
                <NavigationItem
                  id={file.fileId}
                  title={file.name || t('GOOGLE_DRIVE.VIEWER.FILES_PANEL.UNTITLED')}
                  description={googleDriveMimeTypeToReadableName(file.mimeType)}
                  isActive={file.fileId === selectedFileId}
                  onClick={handleFileClick}
                  icon={<DroxyBoxedIcon icon={getFileIcon(file.mimeType)} hasBorder />}
                />
              </Box>
            ))}
          </Stack>
        }
        mainContent={
          selectedGoogleDriveFile && (
            <ExtractedContentPreview
              title={selectedGoogleDriveFile.name || t('GOOGLE_DRIVE.VIEWER.FILES_PANEL.UNTITLED')}
              content={selectedGoogleDriveFile.pages.map((page) => page.text).join('\n\n---\n\n')}
              contentKey={selectedFileId || ''}
              subtitleComponent={
                <Box>
                  <Flex gap={4} align="center">
                    <Anchor
                      style={{ color: theme.colors.gray[6] }}
                      href={webViewLink}
                      target="_blank"
                      rel="noopener noreferrer">
                      {t('GOOGLE_DRIVE.VIEWER.OPEN_IN_GOOGLE_DRIVE')}
                    </Anchor>
                    <IconExternalLink style={{ width: 16, height: 16, color: theme.colors.gray[6] }} />
                  </Flex>
                </Box>
              }
            />
          )
        }
      />
    </ResourceViewerWrapper>
  );
}
